// Shared form controls reused across all calculators.
import { useUnitSystem, fromImperial, toImperial, displayUnit, displayStep } from '../lib/units'

export function Field({
  label, value, onChange, min = 0, step = 1, suffix,
}: {
  label: string
  value: number
  onChange: (n: number) => void
  min?: number
  step?: number
  suffix?: string
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-text-body">{label}</span>
      <span className="flex items-center gap-1 rounded-md border border-border bg-surface px-3 focus-within:border-brand">
        <input
          type="number"
          inputMode="decimal"
          value={Number.isFinite(value) ? value : ''}
          min={min}
          step={step}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="tnum h-11 w-full bg-transparent text-md text-text outline-none"
        />
        {suffix && <span className="text-sm text-text-subtle">{suffix}</span>}
      </span>
    </label>
  )
}

// Unit-system-aware Field. Calculator state stays in IMPERIAL (the canonical
// units expected by every src/calculators/*.ts compute function). When the user
// switches to metric in the header, this field:
//   - displays the imperial-stored value converted to metric (e.g. ft → m)
//   - converts user input from metric back to imperial before storing
//   - swaps the suffix label (ft → m, in → cm, sq ft → m², gal → L, lb → kg)
//   - adjusts the step size to something sensible for the displayed unit
// `imperialUnit` is one of the canonical strings: 'ft', 'in', 'yd', 'sq ft',
// 'sq yd', 'cu ft', 'cu yd', 'gal', 'lb', 'ton'. Other units pass through.
export function SmartField({
  label, imperialValue, onChangeImperial, imperialUnit, step = 1, min = 0,
}: {
  label: string
  imperialValue: number
  onChangeImperial: (n: number) => void
  imperialUnit: string
  step?: number
  min?: number
}) {
  const [system] = useUnitSystem()
  const displayed = fromImperial(imperialValue, imperialUnit, system)
  const suffix = displayUnit(imperialUnit, system)
  const stepShown = displayStep(step, imperialUnit, system)
  // Round display to 3 dp to avoid noisy trailing digits from round-trip conversion.
  const valueShown = Number.isFinite(displayed) ? Math.round(displayed * 1000) / 1000 : ''
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-text-body">{label}</span>
      <span className="flex items-center gap-1 rounded-md border border-border bg-surface px-3 focus-within:border-brand">
        <input
          type="number"
          inputMode="decimal"
          value={valueShown}
          min={min}
          step={stepShown}
          onChange={(e) => {
            const n = parseFloat(e.target.value) || 0
            onChangeImperial(toImperial(n, imperialUnit, system))
          }}
          className="tnum h-11 w-full bg-transparent text-md text-text outline-none"
        />
        <span className="text-sm text-text-subtle">{suffix}</span>
      </span>
    </label>
  )
}

export function Segmented<T extends string>({
  label, value, options, onChange,
}: {
  label: string
  value: T
  options: { v: T; l: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-subtle">{label}</span>
      <div className="inline-flex rounded-md border border-border p-0.5 text-sm">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`rounded px-3 py-1 ${value === o.v ? 'bg-primary text-on-primary' : 'text-text-muted'}`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  )
}
